import React from 'react';
import {View} from 'MWC';
import widgetsHelper from 'utils/getWidget.helper';
import PropTypes from 'prop-types';
import {iURL} from 'MWC';
import cookie from '../../../helpers/vendor/cookie';
import {canUseDOM} from '../../../helpers/canUseDOM';
import {getScrollBehavior} from '../../../helpers/utils/scrollBehavior';

const utmMarks = [
        'utm_manager',
        'utm_source',
        'utm_comment',
        'utm_campaign',
        'utm_medium',
        'utm_term',
        'utm_content'
    ],
    CPA_PROGRAM_SOURCE = 'CPA';

export default class BaseNewCardView extends View {
    constructor(props) {
        super(props);

        this.state = {
            where_by: ['path'],
            search_by: [typeof window !== 'undefined' ? window.location.pathname : '/'],
            widgets: [],
            loader: true,
            showModal: false,
            faqTab: false,
            partnersTab: false,
            detailedInsuranceInfoTab: false,
            intermediaryInsuranceInfoTab: false,
            environment: 'prod',
            model: {}
        };

        this.widgetsHelper = widgetsHelper;
        this.toggleModal = this.toggleModal.bind(this);
    }

    shouldFetchData() {
        return true;
    }

    componentWillMount() {
        if (canUseDOM) return;
        const {ssrData} = this.context;
        if (ssrData && ssrData.widgets && ssrData.widgets.length > 0) {
            this.setState({widgets: ssrData.widgets, loader: false});
        }
    }

    componentDidMount() {
        this.determineEnvironment();
        this.getUrlParams();
        if (this.shouldFetchData()) {
            this.widgetsHelper.getWidgets.call(this);
        }
    }

    componentWillUnmount() {
        document.body.classList.remove('disable-scroll');
    }

    toggleModal() {
        const willOpen = !this.state.showModal;
        this.setState({showModal: willOpen});

        document.body.classList.toggle('disable-scroll', willOpen);
    }

    scrollToContent = () => {
        const element = document.querySelector('.content:not(.hidden)');
        if (element) {
            element.scrollIntoView({behavior: getScrollBehavior(), block: 'start'});
        }
    };

    toggleTabWithConfig = config => {
        if (typeof config === 'string') {
            config = {tabKey: config};
        }

        const {tabKey, shortKey, fullKey} = config;

        this.setState(prevState => {
            const isOpening = !prevState[tabKey];

            if (!shortKey || !fullKey) {
                return {[tabKey]: isOpening};
            }

            return {
                [tabKey]: isOpening,
                [fullKey]: isOpening
                    ? prevState[shortKey].replace('-short', '-full')
                    : prevState[fullKey]
            };
        }, this.scrollToContent);
    };

    toggleFaqTab = () => {
        const hasVariants = 'tabShortQuestions' in this.state && 'tabAllQuestions' in this.state;

        this.toggleTabWithConfig({
            tabKey: 'faqTab',
            shortKey: hasVariants ? 'tabShortQuestions' : undefined,
            fullKey: hasVariants ? 'tabAllQuestions' : undefined
        });
    };

    toggleAllPartnersTab = () => {
        this.toggleTabWithConfig('partnersTab');
        this.setState({partnerTab: 'partner-21'});
    };

    toggleDetailedInsuranceInfoTab = () => {
        const hasVariants =
            'tabShortInsuranceInfo' in this.state && 'tabFullInsuranceInfo' in this.state;

        this.toggleTabWithConfig({
            tabKey: 'detailedInsuranceInfoTab',
            shortKey: hasVariants ? 'tabShortInsuranceInfo' : undefined,
            fullKey: hasVariants ? 'tabFullInsuranceInfo' : undefined
        });
    };

    toggleIntermediaryInsuranceInfoTab = () => {
        const hasVariants =
            'tabShortIntermediaryInsuranceInfo' in this.state &&
            'tabFullIntermediaryInsuranceInfo' in this.state;

        this.toggleTabWithConfig({
            tabKey: 'intermediaryInsuranceInfoTab',
            shortKey: hasVariants ? 'tabShortIntermediaryInsuranceInfo' : undefined,
            fullKey: hasVariants ? 'tabFullIntermediaryInsuranceInfo' : undefined
        });
    };

    scrollConfig() {
        return [];
    }

    handleScrollFromQuery() {
        const config = this.scrollConfig();
        if (!config.length) return;

        const params = new URLSearchParams(window.location.search);

        const match = config.find(item => params.has(item.param));
        if (!match) return;

        this.waitForElement(match.selector, element => {
            element.scrollIntoView({
                behavior: getScrollBehavior(),
                block: 'start'
            });
        });
    }

    scrollToFrequentlyAskedQuestions = e => {
        e.preventDefault();
        const targetElement = document.getElementById('frequentlyAskedQuestions');
        if (targetElement) {
            targetElement.scrollIntoView({behavior: getScrollBehavior(), block: 'start'});
        }
    };

    toggleTab = tab => {
        this.setState({tab: tab});
    };

    saveUTMParams() {
        const params = iURL.getSearchParams();
        let result = {};

        if (params.utm_source) {
            result = this._getUTMMarks(params);
        } else if (cookie('utm_source') === CPA_PROGRAM_SOURCE) {
            result = this._getUTMMarks();
        }

        return result;
    }

    _getUTMMarks(storage = cookie) {
        const result = {};

        for (const name of utmMarks) {
            if (storage === cookie) {
                result[name] = cookie(name) || '';
            } else {
                result[name] = storage[name] || '';
            }
        }

        return result;
    }

    navigate = (...args) => {
        return this.context.app.navigate(...args);
    };

    waitForElement(selector, callback, maxFrames = 300) {
        let frames = 0;
        let lastTop = null;
        let stableFrames = 0;

        const check = () => {
            const element = /^#[\w-]+$/.test(selector)
                ? document.getElementById(selector.slice(1))
                : document.querySelector(selector);

            if (!element) {
                if (frames++ < maxFrames) {
                    requestAnimationFrame(check);
                } else {
                    console.warn(`⚠️ Element ${selector} not found`);
                }
                return;
            }

            const rect = element.getBoundingClientRect();
            const top = rect.top;

            if (lastTop !== null && Math.abs(lastTop - top) < 1) {
                stableFrames++;
            } else {
                stableFrames = 0;
            }

            lastTop = top;

            if (stableFrames >= 2) {
                callback(element);
                return;
            }

            if (frames++ < maxFrames) {
                requestAnimationFrame(check);
            } else {
                console.warn(`⚠️ Element ${selector} did not stabilize`);
            }
        };

        check();
    }

    determineEnvironment() {
        if (window.location.href.includes('a-bank.com.ua')) {
            this.setState({environment: 'prod'});
        } else {
            this.setState({environment: 'release'});
        }
    }

    scrollToAndActivateTab = (config = {}) => {
        const {paramKeys = [], stateKey, selector, onScrollComplete} = config;

        const params = new URLSearchParams(window.location.search);
        const activeKey = paramKeys.find(key => params.has(key));

        if (!activeKey) return;

        const callback = () => {
            if (!selector) {
                onScrollComplete && onScrollComplete();
                return;
            }

            this.waitForElement(selector, element => {
                element.scrollIntoView({
                    block: 'start',
                    behavior: getScrollBehavior()
                });

                onScrollComplete && onScrollComplete(element);
            });
        };

        if (!stateKey) {
            callback();
            return;
        }

        this.setState({[stateKey]: activeKey}, callback);
    };

    getUrlParams() {
        const params = new URLSearchParams(window.location.search);
        const paramsObj = {};

        for (const [key, value] of params) {
            paramsObj[key] = value;
        }
        this.setState({utmParams: paramsObj});
    }

    static contextTypes = {
        app: PropTypes.object,
        history: PropTypes.object,
        theme: PropTypes.string,
        ssrData: PropTypes.object
    };
}
